import { QueryClient } from '@tanstack/react-query'
import { createToast } from '@/utils'
import { showAlertToast } from '@/utils/showAlertToast'
import DialogUploadValues from '@/types/dialogUploadValues'
import { FormikHelpers } from 'formik'
import { Dispatch, SetStateAction } from 'react'
import { UploadOptions } from './useUploadFile'
import handlePush from './handlePush'
import buildPendingFolder from './buildPendingFolder'
import {
  addPendingCreatedFolder,
  setFolderCreationToastId,
} from './pendingCreatedFolders'
import { Configuration } from '@/api'

const FOLDER_LIST_QUERY_KEYS = [
  ['documents'],
  ['dashboardV2', 'activities'],
] as const

export interface Args {
  values: DialogUploadValues
  helpers: FormikHelpers<DialogUploadValues>
  onFilesUploadCompleted: () => void
  uploadFile: (a: UploadOptions) => Promise<void>
  onClose: () => void
  successMessage: string
  isMounted: () => boolean
  setSubmitInProgress: Dispatch<SetStateAction<boolean>>
  useZip?: boolean
  navigate: (path: string) => void
  currentPathname: string
  queryClient: QueryClient
  configurations?: Pick<Configuration, 'cid' | 'groupId'>[]
  /** Appelé une fois les fichiers envoyés, avant de fermer la fenêtre : de quoi montrer la réussite. */
  onUploaded?: () => Promise<void> | void
}

/**
 * Function to handle the file upload process
 *
 */
export default ({
  values,
  helpers,
  onFilesUploadCompleted,
  uploadFile,
  onClose,
  successMessage,
  isMounted,
  setSubmitInProgress,
  useZip,
  navigate,
  currentPathname,
  queryClient,
  configurations,
  onUploaded,
}: Args) => {

  try {
    // Push the batch import request and handle the result
    return handlePush({
      values,
      uploadFile,
      useZip,
      configurations,
    })
      .catch((error: Error) => {
        console.error(error)
        createToast('error', `Error in file upload: ${error.message}`)
        setSubmitInProgress(false)
        throw error
      })
      .then(async (result) => {
        await onUploaded?.()
        if (result?.batchImportID) {
          addPendingCreatedFolder(
            buildPendingFolder(values, result.batchImportID)
          )
        }
        helpers.resetForm()
        FOLDER_LIST_QUERY_KEYS.forEach((queryKey) => {
          void queryClient.invalidateQueries({ queryKey: [...queryKey] })
        })
        onFilesUploadCompleted()
        onClose()
        setSubmitInProgress(false)
        setFolderCreationToastId(showAlertToast.loading(successMessage))
        // On renvoie vers le dossier qu'on vient de créer, pas vers la liste : la page d'entrée
        // est la seule à décider où il s'ouvre. Un dossier agentique tombe sur son écran de
        // suivi — c'est là qu'on veut être juste après l'upload. Un dossier legacy n'a encore
        // rien à montrer : la même page d'entrée le renvoie à la liste, comme avant.
        if (result?.batchImportID) {
          navigate(`/documents/${result.batchImportID}`)
        } else if (currentPathname !== '/documents/list') {
          navigate('/documents/list')
        }
      })
      .catch((error: any) => {
        console.error(error)
        if (isMounted()) {
          helpers.setStatus({ success: false })
          helpers.setSubmitting(false)
          setSubmitInProgress(false)
        }
      })
  } catch (err: any) {
    console.error(err)
    if (isMounted()) {
      helpers.setStatus({ success: false })
      helpers.setSubmitting(false)
      setSubmitInProgress(false)
    }
  }
}
